import { FilterIcon } from "@heroicons/react/outline";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getNoteState } from "../../../components/sessions/helper";
import NoteModel from "../../../api/models/NoteModel";
import { indexStudentNotes } from "../../../api/endpoints/students/notes";
import { makeApiRequest, showApiErrorMessages } from "../../../helpers";
import { ApiResponse } from "../../../api/apiResponse";
import AppFilters from "../../../components/app-filters/AppFilters";
import ShowIf from "../../../components/show-if/ShowIf";
import TableHeaderCell from "../../../components/table-cell/TableHeaderCell";
import { useOrderBy } from "../../../hooks/useOrderBy";
import { ApiCommonParams } from "../../../api/@types/apiTypes";
import Pagination from "../../../components/pagination/Pagination";
import { ApiPagination } from "../../../api/helpers/apiPagination";
import { useInfiniteStudentSessions } from "../../../hooks/useInfiniteStudentSessions";
import { useQuery } from "react-query";
import { indexStudentSessions } from "../../../api/endpoints/students/sessions";

function NotesViewPage() {
  const LocalBasePath = "page.student.dashboard.notes";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<{ [key: string]: any }>({});
  const { orderBy, getSortingDirection, handleSetSortingOrder } = useOrderBy();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const resetCurrentPage = () => {
    setCurrentPage(1);
  };
  let [currentPageFilter, setCurrentPageFilter] = useState<{
    [key: string]: any;
  }>({
    currentSessionsPage: 1,
  });

  const { data: notes } = useQuery(
    ["studentNotes", currentPage, filters, orderBy],
    () => {
      let apiParams: ApiCommonParams = {
        filters,
        orderBy,
        pagination: { pageSize: 10, pageNumber: currentPage },
      };
      return indexStudentNotes(apiParams);
    },
    {
      keepPreviousData: true,
      select: (response) => {
        let fetchedNotes: NoteModel[] = [];
        if (response.isSuccess) {
          fetchedNotes = NoteModel.fromApiResponse(response.data);
        } else {
          showApiErrorMessages(response);
        }
        const paginationInfo = response.pagination;
        return { data: fetchedNotes, paginationInfo };
      },
    }
  );

  const { sessions, loadMoreSessions } =
    useInfiniteStudentSessions(indexStudentSessions);

  useEffect(() => {
    resetCurrentPage();
  }, [filters, orderBy]);

  useEffect(() => {
    loadMoreSessions();
  }, [currentPageFilter.currentSessionsPage]);
  const statusOptions = [
    {
      key: t("passed"),
      value: "passed",
    },
    {
      key: t("unpassed"),
      value: "unpassed",
    },
  ];
  const sessionsOptions = sessions.reverse().map((session) => ({
    key: `${t("session")} #${session.sessionNumber}`,
    value: `${session.id}`,
  }));

  const onCancelFilters = () => {
    setShowFilters(false);
    setFilters({});
  };
  return (
    <div className="w-full min-h-screen  flex flex-col justify-between">
      <div className="">
        <div className="bg-gray-50 p-4 ring-1 ring-gray-300 divide-gray-300 text-gray-900   sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <h2 className="text-2xl	">{t("recitationNotes")}</h2>
          <button
            type="button"
            data-cy="filterNotes"
            onClick={() => setShowFilters((x) => !x)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
          >
            {t("filter")}
            <FilterIcon
              className="ml-3 -mr-1 rtl:mr-3 rtl:-ml-1 h-5 w-5"
              aria-hidden="true"
            />
          </button>
        </div>
        <ShowIf condition={showFilters}>
          {/**
           * you can add (loadingBottomMargin:number) if you need to load data before scrollbar have specific space to end
           */}
          <AppFilters
            className="bg-gray-50 pt-1 pb-5 px-4 flex flex-col sm:flex-row gap-4"
            filters={filters}
            setFilters={setFilters}
            onLoadMore={(key: string) =>
              setCurrentPageFilter({
                ...currentPageFilter,
                [key]: ++currentPageFilter[key],
              })
            }
            fields={[
              {
                name: "state",
                label: t("status"),
                options: statusOptions,
              },
              {
                name: "session_id",
                paginationLabel: "currentSessionsPage",
                label: t("session"),
                options: sessionsOptions,
              },
            ]}
            handleButtonClick={onCancelFilters}
          />
        </ShowIf>
        <div className="w-full h-px bg-gray-200" />
        {notes?.data?.length! > 0 ? (
          <>
            <table
              data-cy="notesTable"
              className="min-w-full divide-y table-fixed  divide-gray-300 "
            >
              <thead className="bg-gray-50">
                <tr>
                  <TableHeaderCell
                    className="w-[128px] text-justify"
                    sortable={true}
                    sortingDirection={getSortingDirection("session_id")}
                    onClick={handleSetSortingOrder("session_id")}
                  >
                    {t("session")}
                  </TableHeaderCell>
                  <TableHeaderCell className="text-justify">
                    {t("note")}
                  </TableHeaderCell>
                  <TableHeaderCell
                    className="w-[128px] text-justify"
                    sortable={true}
                    sortingDirection={getSortingDirection("state")}
                    onClick={handleSetSortingOrder("state")}
                  >
                    {t("status")}
                  </TableHeaderCell>
                </tr>
              </thead>
              <tbody>
                {notes?.data.map((note, index) => {
                  return (
                    <tr
                      key={`NoteInfoTable_${index}_note`}
                      className={" border-t "}
                    >
                      <td className={" px-3 py-3.5  lg:table-cell"}>
                        <h6 className="text-sm text-gray-900">
                          {note?.session?.sessionNumber!}#
                        </h6>
                        <h6 className="text-xs text-gray-500">
                          {note?.created_at?.format("DD/MM/YYYY")}
                        </h6>
                      </td>
                      <td className={"relative py-4 pl-4 sm:pl-6 pr-3 text-sm"}>
                        <div className="font-medium text-gray-500">
                          {note.note}
                        </div>
                      </td>
                      <td
                        className={
                          " px-3 py-3.5 text-sm text-gray-500 lg:table-cell"
                        }
                      >
                        {getNoteState(note?.status!)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        ) : (
          <>
            <div className="flex h-[50vh] justify-center items-end">
              <h1>{t("noresultsfound")}</h1>
            </div>
          </>
        )}
      </div>
      <div className="sticky mt-auto bottom-0">
        <Pagination
          hasNext={!!notes?.paginationInfo?.hasNext}
          hasPrev={!!notes?.paginationInfo?.hasPrev}
          goToPage={(page: number) => setCurrentPage(page)}
          currentPage={Number(notes?.paginationInfo?.currentPageNumber || 0)}
          numberOfPages={Number(notes?.paginationInfo?.pagesCount || 0)}
        />
      </div>
    </div>
  );
}
export default NotesViewPage;
